import React from 'react';
import {
  isHomeItem,
  megaMenuPanelId,
  menuItemKey,
  withHomeItem,
  type MenuItem,
} from './mainMenu';
import { SearchForm } from './SearchForm';
import {
  CaretDownIcon,
  ListIcon,
  MagnifyingGlassIcon,
  XIcon,
} from '@phosphor-icons/react';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers';
import config from '@plone/volto/registry';
import { UniversalLink } from '@plone/volto/components';
import { NavLink } from 'react-router-dom';
import HouseIcon from 'volto-ministerio-publico-federal/components/Icons/House';
import {
  MPFButton,
  MPFIconButton,
} from 'volto-ministerio-publico-federal/components/UI';

export interface MenuProps {
  navItems: MenuItem[];
  navRef: React.RefObject<HTMLElement | null>;
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  mobileOpen: boolean;
  onToggleMobile: () => void;
  burgerRef: React.RefObject<HTMLButtonElement | null>;
  searchOpen: boolean;
  onToggleSearch: () => void;
  searchToggleRef: React.RefObject<HTMLButtonElement | null>;
}

export interface MenuLinkProps {
  node: MenuItem;
  className?: string;
  role?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  children?: React.ReactNode;
}

export const MenuLink: React.FC<MenuLinkProps> = ({
  node,
  className,
  role,
  onClick,
  children,
}) => {
  const { settings } = config;
  const rawUrl = node.url ?? node.href ?? '';
  const targetUrl = rawUrl === '' ? '/' : flattenToAppURL(rawUrl);
  const label = children ?? node.title;

  if (isInternalURL(rawUrl) || rawUrl === '') {
    return (
      <NavLink
        to={targetUrl}
        className={className}
        role={role}
        onClick={onClick}
        activeClassName="active"
        exact={targetUrl === '/'}
      >
        {label}
      </NavLink>
    );
  }

  return (
    <UniversalLink
      href={rawUrl}
      className={className}
      {...({
        role,
        onClick,
        rel: 'noopener noreferrer',
        target: settings?.openExternalLinkInNewTab ? '_blank' : '_self',
      } as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
    >
      {label}
    </UniversalLink>
  );
};

export const Menu: React.FC<MenuProps> = ({
  navItems,
  navRef,
  openDropdown,
  setOpenDropdown,
  triggerRefs,
  mobileOpen,
  onToggleMobile,
  burgerRef,
  searchOpen,
  onToggleSearch,
  searchToggleRef,
}) => {
  return (
    <div id="navigation" tabIndex={-1} className="mpf-topbar__bottombar">
      <div className="mpf-topbar__bottombar_container">
        {}
        <MPFIconButton
          ref={burgerRef}
          variant="unstyled"
          shape="square"
          className="mpf-topbar__burger-button"
          aria-controls="mobile-menu"
          aria-expanded={mobileOpen}
          label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          onClick={onToggleMobile}
          icon={
            mobileOpen ? (
              <XIcon className="mpf-topbar__burger-icon" />
            ) : (
              <ListIcon className="mpf-topbar__burger-icon" />
            )
          }
        />

        <nav
          className="mpf-topbar__nav"
          aria-label="Navegação principal"
          ref={navRef}
        >
          <ul className="mpf-topbar__menu">
            {withHomeItem(navItems).map((item) => {
              const hasChildren = item.items && item.items.length > 0;

              const itemId = menuItemKey(item);

              if (!hasChildren) {
                const isHome = isHomeItem(item);

                return (
                  <li key={itemId} className="mpf-topbar__menu-item">
                    <MenuLink
                      node={item}
                      className={`mpf-topbar__nav-link${
                        isHome ? ' mpf-topbar__nav-link--home' : ''
                      }`}
                    >
                      {isHome ? (
                        <>
                          <HouseIcon
                            className="mpf-topbar__home-icon"
                            aria-hidden="true"
                          />
                          <span className="sr-only">{item.title}</span>
                        </>
                      ) : undefined}
                    </MenuLink>
                  </li>
                );
              }

              const isOpen = openDropdown === itemId;

              return (
                <li key={itemId} className="mpf-topbar__menu-item">
                  {}
                  <MPFButton
                    variant="unstyled"
                    ref={(element) => {
                      triggerRefs.current[itemId] =
                        element as HTMLButtonElement | null;
                    }}
                    className={`mpf-topbar__nav-button${
                      isOpen ? ' mpf-topbar__nav-button--active' : ''
                    }`}
                    aria-expanded={isOpen}
                    aria-controls={megaMenuPanelId(itemId)}
                    onClick={() =>
                      setOpenDropdown((prev) =>
                        prev === itemId ? null : itemId,
                      )
                    }
                  >
                    <span>{item.title}</span>
                    <CaretDownIcon
                      className={`mpf-topbar__chevron${
                        isOpen ? ' mpf-topbar__chevron--open' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </MPFButton>
                </li>
              );
            })}
          </ul>
        </nav>

        <SearchForm variant="desktop" />

        {searchOpen ? (
          <SearchForm variant="bar" focusOnMount />
        ) : (
          <MPFIconButton
            ref={searchToggleRef}
            variant="unstyled"
            shape="square"
            className="mpf-topbar__search-toggle"
            aria-controls="site-search-bar"
            aria-expanded={searchOpen}
            label="Abrir busca"
            onClick={onToggleSearch}
            icon={<MagnifyingGlassIcon className="mpf-topbar__search-icon" />}
          />
        )}
      </div>
    </div>
  );
};

export default Menu;
